import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import fromPairs from 'lodash/fromPairs';
import isUndefined from 'lodash/isUndefined';
import { Button } from '@els/els-react--button';
import FactsContext from '@Components/Results/substances/contexts/facts.context';
import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceHitDataModal from '@Components/Results/substances/information/SubstanceHitDataModal';
import { getFactCount } from '@Components/Results/substances/utils/factCountHelper';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import NumberFormatter from '@Utils/NumberFormatter';

function buildFactsMap(facts) {
  if (!facts) return null;
  return fromPairs(facts.map((fact) => [fact.id, { ...fact, open: Boolean(fact.hitData) }]));
}

function SubstanceHitDataButton({ metadata = {}, structure, showFooter = false }) {
  const [facts, setFacts] = useState(() => buildFactsMap(structure?.facts));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const database = useMemo(
    () => SubstanceHelper.getCurrentDatabase(metadata?.databaseId || structure?.databaseId),
    [metadata?.databaseId, structure]
  );
  const hitDataFact = useMemo(() => find(facts, (fact) => fact.hitData), [facts]);

  const toggleFact = useCallback(
    (id, open) => {
      if (!facts?.[id]) return Promise.resolve(null);

      const currentFact = { ...facts[id], open: isUndefined(open) ? !facts[id].open : open };
      setFacts((prev) => ({ ...prev, [id]: currentFact }));

      return Promise.resolve(SubstanceHelper.checkAndRetrieveFactData(currentFact, structure, metadata))
        .then(() => {
          if (!isMountedRef.current) return;
          setFacts((prev) => ({ ...prev, [id]: { ...prev[id], ...currentFact } }));
        })
        .catch(Logger.reject);
    },
    [facts, structure, metadata]
  );

  const openModal = useCallback(() => {
    if (!hitDataFact) return;
    toggleFact(hitDataFact.id, true);
    setIsModalOpen(true);
  }, [hitDataFact, toggleFact]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const factsContextValue = useMemo(() => ({ facts, toggle: toggleFact }), [facts, toggleFact]);

  // Not a substance card (no facts), or no substance in the result set has hit data — render nothing.
  // When at least one substance has hit data (showFooter), keep the footer on every card for alignment,
  // showing the button only on the cards that actually have hit data.
  if (!structure?.facts || !showFooter) return null;

  const count = hitDataFact ? getFactCount(hitDataFact, structure) : 0;

  return (
    <FactsContext.Provider value={factsContextValue}>
      <div className="substance-image-card__footer">
        {hitDataFact && (
          <Button
            data-e2e="substance-grid-show-hit-data"
            className="substance-image-card__hit-data-button"
            type="secondary"
            size="small"
            onClick={openModal}>
            Show hit data{count ? ` - ${NumberFormatter(count, 0)}` : ''}
          </Button>
        )}
      </div>

      {isModalOpen && hitDataFact && (
        <SubstanceHitDataModal
          metadata={metadata}
          substance={structure}
          database={database}
          fact={facts[hitDataFact.id]}
          onClose={closeModal}
        />
      )}
    </FactsContext.Provider>
  );
}

SubstanceHitDataButton.propTypes = {
  metadata: MetadataPropTypesShape,
  structure: PropTypes.object.isRequired,
  showFooter: PropTypes.bool,
};

export default SubstanceHitDataButton;
